/**
 * Metric resolution — Database Schema §9, UX/UI Specification §11.
 *
 * The rule the whole product turns on: a metric's history is its own. When a
 * value is missing from a record, that record is skipped for that metric and
 * the search continues backwards. An instructor who measured only the waist in
 * June has not erased May's hip measurement.
 *
 *   June 1   waist 90
 *   June 15  waist not taken
 *   July 1   waist 86      ->  change is -4, measured against June 1
 *
 * Implemented once here because the overview tiles, the trend charts and the
 * report all have to agree. Two implementations of this would eventually
 * disagree, and the one that shows a wrong number to a paying client is the
 * one nobody tested.
 */

import {
  METRICS,
  METRIC_TABLE_ORDER,
  muscleLabel,
  muscleUnitSymbol,
  type MetricKey,
  type MuscleUnit,
} from '../constants/metrics.js';
import { calculateBmi } from '../format/tr.js';

/**
 * A measurement the product does not name — a wrist, a calf, a neck.
 *
 * The label and unit travel with the value rather than living in a registry of
 * metric definitions: a registry would need a management screen, rename
 * semantics, and a decision about old values when a definition changes. This
 * carries what the instructor wrote at the moment they wrote it.
 */
export interface CustomValue {
  /** As the instructor wrote it. This is what the report prints. */
  label: string;
  /** Turkish-folded, so one visit's value matches the next one's. */
  labelNormalized: string;
  unit: string;
  value: number;
}

export interface MeasurementRecord {
  id: string;
  /** YYYY-MM-DD */
  measuredAt: string;
  weightKg: number | null;
  waistCm: number | null;
  hipCm: number | null;
  chestCm: number | null;
  rightArmCm: number | null;
  leftArmCm: number | null;
  rightThighCm: number | null;
  leftThighCm: number | null;
  bodyFatPct: number | null;
  muscleValue: number | null;
  muscleUnit: MuscleUnit | null;
  /** Empty for a visit with no extra measurements, which is most of them. */
  custom?: CustomValue[];
}

/**
 * Every custom metric this student has, newest label wins for display.
 *
 * Matched on the folded label, so "El Bileği" and "el bilegi" are one metric
 * across visits rather than two that each have half the history.
 */
export function customMetricsOf(
  records: readonly MeasurementRecord[],
): { labelNormalized: string; label: string; unit: string }[] {
  const found = new Map<
    string,
    { labelNormalized: string; label: string; unit: string }
  >();

  // Oldest first so a later spelling or unit replaces an earlier one.
  const ordered = [...records].sort((a, b) => (a.measuredAt < b.measuredAt ? -1 : 1));
  for (const record of ordered) {
    for (const value of record.custom ?? []) {
      found.set(value.labelNormalized, {
        labelNormalized: value.labelNormalized,
        label: value.label,
        unit: value.unit,
      });
    }
  }

  return [...found.values()].sort((a, b) => a.label.localeCompare(b.label, 'tr'));
}

/**
 * The series for one custom metric, following the same null-gap rule as the
 * built-in ones: a visit that did not measure it is skipped, not zeroed.
 */
export function customMetricSeries(
  records: readonly MeasurementRecord[],
  labelNormalized: string,
): MetricPoint[] {
  return [...records]
    .sort((a, b) => (a.measuredAt < b.measuredAt ? -1 : 1))
    .flatMap((record) => {
      const found = (record.custom ?? []).find(
        (value) => value.labelNormalized === labelNormalized,
      );
      if (!found) return [];
      return [
        { measurementId: record.id, measuredAt: record.measuredAt, value: found.value },
      ];
    });
}

export interface MetricPoint {
  measurementId: string;
  measuredAt: string;
  value: number;
  /** Only set for the muscle metric, which can be a percentage or kilograms. */
  unit?: MuscleUnit;
}

/** Why a comparison was withheld even though two values exist. */
export type BlockedReason = 'MUSCLE_UNIT_MISMATCH';

export interface MetricSummary {
  key: MetricKey;
  /** Resolved Turkish label. Muscle reads differently in kg than in percent. */
  label: string;
  unit: string;
  decimals: number;
  current: MetricPoint;
  previous: MetricPoint | null;
  /** current - previous; null when there is nothing to compare against. */
  changeAbs: number | null;
  blockedReason: BlockedReason | null;
}

const RAW_VALUE: Record<Exclude<MetricKey, 'bmi' | 'muscle'>, keyof MeasurementRecord> = {
  weight: 'weightKg',
  waist: 'waistCm',
  hip: 'hipCm',
  chest: 'chestCm',
  rightArm: 'rightArmCm',
  leftArm: 'leftArmCm',
  rightThigh: 'rightThighCm',
  leftThigh: 'leftThighCm',
  bodyFat: 'bodyFatPct',
};

/** Oldest first. Ties broken by id so the order is stable across calls. */
function chronological(records: readonly MeasurementRecord[]): MeasurementRecord[] {
  return [...records].sort((a, b) => {
    if (a.measuredAt !== b.measuredAt) return a.measuredAt < b.measuredAt ? -1 : 1;
    return a.id < b.id ? -1 : 1;
  });
}

function isUsable(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

/**
 * Every recorded value for one metric, oldest first, records missing that
 * metric skipped entirely.
 *
 * `heightCm` is needed only for BMI, which is derived rather than stored
 * (Schema §23). Without a usable height, BMI yields no points at all rather
 * than dividing by zero.
 */
export function metricSeries(
  records: readonly MeasurementRecord[],
  key: MetricKey,
  heightCm?: number | null,
): MetricPoint[] {
  const points: MetricPoint[] = [];

  for (const record of chronological(records)) {
    if (key === 'bmi') {
      if (!isUsable(record.weightKg) || !isUsable(heightCm)) continue;
      const bmi = calculateBmi(record.weightKg, heightCm);
      if (bmi === null) continue;
      points.push({
        measurementId: record.id,
        measuredAt: record.measuredAt,
        value: bmi,
      });
      continue;
    }

    if (key === 'muscle') {
      if (!isUsable(record.muscleValue) || !record.muscleUnit) continue;
      points.push({
        measurementId: record.id,
        measuredAt: record.measuredAt,
        value: record.muscleValue,
        unit: record.muscleUnit,
      });
      continue;
    }

    const value = record[RAW_VALUE[key]];
    if (!isUsable(value)) continue;
    points.push({
      measurementId: record.id,
      measuredAt: record.measuredAt,
      value,
    });
  }

  return points;
}

/** The newest recorded value for a metric, or null if never recorded. */
export function latestValue(
  records: readonly MeasurementRecord[],
  key: MetricKey,
  heightCm?: number | null,
): MetricPoint | null {
  const series = metricSeries(records, key, heightCm);
  return series.length > 0 ? (series[series.length - 1] as MetricPoint) : null;
}

/** The student's first recorded value for a metric — "Başlangıç" (D-01). */
export function firstValue(
  records: readonly MeasurementRecord[],
  key: MetricKey,
  heightCm?: number | null,
): MetricPoint | null {
  const series = metricSeries(records, key, heightCm);
  return series.length > 0 ? (series[0] as MetricPoint) : null;
}

/**
 * The most recent value for a metric strictly before the newest one.
 *
 * This is what an overview tile compares against, and it is where the
 * null-skipping matters: it is the previous value *of this metric*, not the
 * previous measurement record.
 */
export function previousValue(
  records: readonly MeasurementRecord[],
  key: MetricKey,
  heightCm?: number | null,
): MetricPoint | null {
  const series = metricSeries(records, key, heightCm);
  return series.length > 1 ? (series[series.length - 2] as MetricPoint) : null;
}

function summarize(
  records: readonly MeasurementRecord[],
  key: MetricKey,
  heightCm: number | null,
): MetricSummary | null {
  const series = metricSeries(records, key, heightCm);
  if (series.length === 0) return null;

  const current = series[series.length - 1] as MetricPoint;
  const previous = series.length > 1 ? (series[series.length - 2] as MetricPoint) : null;

  const definition = METRICS[key];
  const isMuscle = key === 'muscle';

  const label = isMuscle && current.unit ? muscleLabel(current.unit) : definition.label;
  const unit =
    isMuscle && current.unit ? muscleUnitSymbol(current.unit) : definition.unit;

  // Subtracting a percentage from a weight would produce a plausible-looking
  // number with no meaning, in a document that goes to a client.
  const unitsDiffer = isMuscle && previous !== null && previous.unit !== current.unit;

  return {
    key,
    label,
    unit,
    decimals: definition.decimals,
    current,
    previous: unitsDiffer ? null : previous,
    changeAbs: previous && !unitsDiffer ? current.value - previous.value : null,
    blockedReason: unitsDiffer ? 'MUSCLE_UNIT_MISMATCH' : null,
  };
}

/**
 * Every metric the student has a value for, in the fixed anatomical order the
 * report uses. Metrics never recorded are absent rather than empty — the
 * product never shows a blank row (UX/UI Specification §10.2).
 */
export function summarizeMetrics(
  records: readonly MeasurementRecord[],
  heightCm: number | null,
): MetricSummary[] {
  const summaries: MetricSummary[] = [];
  for (const key of METRIC_TABLE_ORDER) {
    const summary = summarize(records, key, heightCm);
    if (summary) summaries.push(summary);
  }
  return summaries;
}

/**
 * The weight that belongs on the student profile — Schema §24.
 *
 * Returns null when no measurement carries a weight, in which case the caller
 * keeps the weight captured at student creation. Deleting or back-dating the
 * newest weight measurement must run this again; taking "the weight that was
 * just saved" would leave the profile showing a value that no longer exists.
 */
export function resolveCurrentWeight(
  records: readonly MeasurementRecord[],
): number | null {
  const latest = latestValue(records, 'weight');
  return latest ? latest.value : null;
}

/** A measurement must carry at least one value to be worth storing. */
export function hasAnyValue(
  record: Omit<MeasurementRecord, 'id' | 'measuredAt'>,
): boolean {
  return (
    isUsable(record.weightKg) ||
    isUsable(record.waistCm) ||
    isUsable(record.hipCm) ||
    isUsable(record.chestCm) ||
    isUsable(record.rightArmCm) ||
    isUsable(record.leftArmCm) ||
    isUsable(record.rightThighCm) ||
    isUsable(record.leftThighCm) ||
    isUsable(record.bodyFatPct) ||
    (isUsable(record.muscleValue) && record.muscleUnit !== null)
  );
}
